export type Extreme = { value: number; date: string; at: string | null };

export type Spell = {
  from: string;
  to: string;
  days: number;
  intense: number;
  peak: number;
  total: number;
  ongoing: boolean;
};

export type SpellHighlight = Spell & { agoDays: number };

/** Eén contextcijfer onder de grote kaart; `unit` bepaalt de opmaak. */
export type Stat = {
  label: string;
  value: number | null;
  unit: '°C' | 'mm' | 'dagen' | 'datum' | 'aantal';
  date?: string | null;
};

export type SpellGroup = {
  kind: string;
  label: string;
  note: string;
  spells: Spell[];
  highlight: SpellHighlight | null;
  stats: Stat[];
};

export type SeasonCard = {
  key: 'winter' | 'spring' | 'summer' | 'autumn';
  label: string;
  elapsedDays: number;
  stats: Stat[];
  comparison: { metric: string; value: number; median: number; seasons: number } | null;
};

export type SpellsResponse = {
  groups: SpellGroup[];
  /** Welk soort bij het openen op de kaart hoort; null → de seizoenskaart. */
  suggested: string | null;
  season: SeasonCard | null;
};

export type Summary = {
  now: { temperature: number | null; measuredAt: number | null; stale: boolean };
  today: {
    date: string | null;
    min: number | null;
    max: number | null;
    minAt: string | null;
    maxAt: string | null;
  };
  anomaly: { value: number | null; normal: number | null };
  record: { value: number | null; date: string | null; gap: number | null; pct: number | null };
  rain: {
    year: number;
    total: number | null;
    medianToDate: number | null;
    deltaPct: number | null;
    cumulative: { day: number; value: number }[];
  };
  heat: { from: string; to: string; days: number; hotDays: number; peak: number; ongoing: boolean } | null;
  today_rain: number | null;
  /** Neerslag over de lopende regendag (07:00 gisteren tot 07:00); null als de reeks niet terugreikt. */
  today_rain_day: number | null;
  pressure: number | null;
  month: { name: string; average: number | null; normal: number | null };
  sun: { sunrise: number | null; sunset: number | null };
  intraday: { ts: number; value: number }[];
  /** Natte halfuren van vandaag: `ts` is het begin van het halfuur, `value` in mm. */
  intradayRain: { ts: number; value: number }[];
  /** Vroegste dag waarvoor de ruwe reeks nog bestaat; ondergrens voor het bladeren. */
  intradayFrom: string | null;
  measuredDays: number;
  /** Alleen op /live: of er voor dit verzoek bij Netatmo is opgehaald. */
  live?: { refreshed: boolean; ageSeconds: number | null; note: string | null };
};

/**
 * Eén dag zoals de curve in de hero hem nodig heeft.
 *
 * `/summary` levert deze velden al voor vandaag mee; `/day` geeft ze voor een
 * dag naar keuze, zodat je op de curve kunt terugbladeren zonder de rest van de
 * hero opnieuw op te halen.
 */
export type DayDetail = {
  date: string;
  min: number | null;
  max: number | null;
  minAt: string | null;
  maxAt: string | null;
  sun: { sunrise: number | null; sunset: number | null };
  intraday: { ts: number; value: number }[];
  intradayRain: { ts: number; value: number }[];
  /** Alle neerslag van die dag in mm; null als er geen regenmeter of geen meting is. */
  rainTotal: number | null;
  /**
   * Dezelfde neerslag over de meteorologische regendag: 07:00 de dag voordien
   * tot 07:00. Null zodra de halfuurreeks niet ver genoeg terugreikt.
   */
  rainDayTotal: number | null;
  intradayFrom: string | null;
};

export type Overview = {
  years: number[];
  coverage: { from: string; to: string } | null;
  has: { outdoor: boolean; rain: boolean; wind: boolean };
};

export type DailyTemp = {
  date: string;
  min: number | null;
  max: number | null;
  avg: number | null;
  minAt: string | null;
  maxAt: string | null;
};

export type YearSeries = { year: number; points: { day: number; value: number }[] };

export type ClimatologyPoint = {
  day: number;
  p10: number;
  p50: number;
  p90: number;
  min: number;
  max: number;
};

export type Compare = { metric: string; series: YearSeries[]; normal: ClimatologyPoint[] };

export type PairedRecord = {
  key: string;
  label: string;
  unit: string;
  tone: 'warm' | 'cool' | 'neutral';
  thisYear: Extreme | null;
  ever: Extreme | null;
};

export type RecordsResponse = {
  year: number;
  paired: PairedRecord[];
  perYear: {
    year: number;
    frostDays: number;
    iceDays: number;
    summerDays: number;
    tropicalDays: number;
    tropicalNights: number;
    rainDays: number;
    rainTotal: number | null;
    measuredDays: number;
  }[];
};

export type RainResponse = {
  granularity: string;
  totals: { period: string; value: number }[];
  cumulative: { year: number; points: { day: number; value: number }[] }[];
};

export type Station = {
  name: string | null;
  city: string | null;
  country: string | null;
  latitude: number | null;
  longitude: number | null;
  altitude: number | null;
  modules: { type: string; name: string | null }[];
} | null;

export type ForecastDay = {
  date: string;
  max: number | null;
  min: number | null;
  rain: number | null;
  code: number | null;
};

export type Forecast = { days: ForecastDay[]; error: string | null };

/**
 * De archiefroutes staan op een half uur browsercache (zie de cache-control in
 * server/src/routes/api.ts). Bij een verversing na lange afwezigheid moet die
 * overgeslagen worden — anders geeft de browser precies terug wat er al op het
 * scherm staat, en heeft het ophalen niets uitgehaald.
 *
 * Een modulevlag in plaats van een parameter: de drie grafieksecties halen hun
 * data zelf op, diep in hun bouwfunctie. Een `fresh`-argument zou door al die
 * handtekeningen heen moeten voor één schakelaar die maar op twee momenten
 * anders staat.
 */
let cacheMode: RequestCache = 'default';

/** Voert `run` uit met de browsercache overgeslagen voor elke ophaling erin. */
export async function fetchingFresh<T>(run: () => Promise<T>): Promise<T> {
  cacheMode = 'reload';
  try {
    return await run();
  } finally {
    cacheMode = 'default';
  }
}

async function get<T>(path: string, params: Record<string, string | undefined> = {}): Promise<T> {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, value);
  }
  const query = search.toString();
  const response = await fetch(`/api${path}${query ? `?${query}` : ''}`, { cache: cacheMode });
  if (!response.ok) throw new Error(`API ${path} gaf ${response.status}`);
  return (await response.json()) as T;
}

export type IntradayPoint = { ts: number; value: number };

/**
 * Wat de pagina van de coverfoto moet weten zonder hem al op te halen.
 *
 * `luminance` (0..1) stuurt de sterkte van de sluier, `updatedAt` maakt de URL
 * uniek per foto zodat een vervanging meteen doorkomt.
 */
export type CoverMeta = {
  width: number;
  height: number;
  luminance: number;
  /** Hoe bont de foto is; samen met `luminance` bepaalt dit de sluier. */
  spread: number;
  updatedAt: number;
};

/**
 * Instellingen die voor iedereen gelden, tegenover het thema, dat een
 * bezoekersvoorkeur is en in localStorage blijft. Zie CONTEXT.md.
 */
export type SiteSettings = { weatherBackground: boolean; cover: CoverMeta | null };

/** Waarmee de pagina begint als de server nog niets gezegd heeft. */
export const DEFAULT_SITE_SETTINGS: SiteSettings = { weatherBackground: true, cover: null };

export type AccountUser = { id: number; email: string; createdAt?: number };

/**
 * Verzoeken die iets veranderen.
 *
 * Apart van `get`: die is voor publieke leesroutes met een cachestand, terwijl
 * dit altijd verse, ondertekende verzoeken zijn. De sessiecookie gaat vanzelf
 * mee — hij staat op dezelfde oorsprong en is `httpOnly`, dus er valt hier
 * niets te bewaren of mee te sturen.
 *
 * De foutmelding van de server wordt doorgegeven in plaats van een statuscode:
 * "Er bestaat al een gebruiker met dat e-mailadres" helpt, "API gaf 409" niet.
 */
async function send<T>(method: string, path: string, body?: unknown): Promise<T> {
  const response = await fetch(`/api${path}`, {
    method,
    ...(body === undefined
      ? {}
      : { headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }),
  });

  const text = await response.text();
  let data: unknown = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    /* geen JSON terug; dan blijft de statuscode het enige wat we hebben */
  }

  if (!response.ok) {
    const message = (data as { error?: unknown }).error;
    throw new Error(typeof message === 'string' ? message : `API ${path} gaf ${response.status}`);
  }
  return data as T;
}

/**
 * Een bestand versturen.
 *
 * Apart van `send`: dit gaat als multipart en niet als JSON, en de browser moet
 * de `content-type` met zijn eigen boundary zelf zetten — die er hier bijzetten
 * levert een verzoek op dat de server niet kan uitpakken.
 */
async function upload<T>(path: string, file: File): Promise<T> {
  const body = new FormData();
  body.append('file', file);

  const response = await fetch(`/api${path}`, { method: 'POST', body });
  const text = await response.text();
  let data: unknown = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    /* geen JSON terug; dan blijft de statuscode het enige wat we hebben */
  }

  if (!response.ok) {
    const message = (data as { error?: unknown }).error;
    throw new Error(typeof message === 'string' ? message : `API ${path} gaf ${response.status}`);
  }
  return data as T;
}

export const api = {
  overview: () => get<Overview>('/overview'),
  intraday: (days: number) => get<IntradayPoint[]>('/intraday', { days: String(days) }),
  summary: () => get<Summary>('/summary'),
  day: (date: string) => get<DayDetail>('/day', { date }),
  /** Zelfde vorm als /summary, maar haalt eerst een verse meting bij Netatmo. */
  live: () => get<Summary>('/live'),
  daily: (from?: string, to?: string) => get<DailyTemp[]>('/daily', { from, to }),
  compare: (metric: string, years: number[]) =>
    get<Compare>('/compare', { metric, years: years.join(',') }),
  records: (year?: number) => get<RecordsResponse>('/records', { year: year?.toString() }),
  spells: () => get<SpellsResponse>('/spells'),
  rain: (granularity: string) => get<RainResponse>('/rain', { granularity }),
  station: () => get<Station>('/station'),
  forecast: () => get<Forecast>('/forecast'),

  settings: () => get<SiteSettings>('/settings'),
  saveSettings: (patch: { weatherBackground?: boolean }) =>
    send<SiteSettings>('PUT', '/settings', patch),

  /** De coverfoto zelf; `updatedAt` erbij zodat een vervanging niet uit de cache komt. */
  coverUrl: (cover: CoverMeta) => `/api/cover?v=${cover.updatedAt}`,
  uploadCover: (file: File) => upload<{ cover: CoverMeta }>('/cover', file),
  removeCover: () => send<{ ok: true }>('DELETE', '/cover'),

  // Langs `send` en niet langs `get`: die laatste is voor publieke leesroutes
  // met een cachestand en gooit een kale statuscode. Hier wil je de tekst van de
  // server zien — "Niet aangemeld." leest beter dan "API /users gaf 401".
  me: () => send<{ user: AccountUser | null }>('GET', '/auth/me'),
  login: (email: string, password: string) =>
    send<{ user: AccountUser }>('POST', '/auth/login', { email, password }),
  logout: () => send<{ ok: true }>('POST', '/auth/logout'),

  /** Wijzigt het wachtwoord van wie is aangemeld; een ander kan er niet zijn. */
  setPassword: (password: string) => send<{ ok: true }>('PUT', '/auth/password', { password }),
};
